import React, { PureComponent } from "react";
import PropTypes from "prop-types";
import accounting from "accounting";
import { plural } from "core/utils/stringUtils";
import { PriceContainer } from "./styles";

const FilteredPrice = props => {
	const { totalPrice, numCycles, cardFrequency, currencySymbol, discountedPrice } = props;
	const cardFrequencyLong = plural(cardFrequency, numCycles);
	const cardFrequencyDisplay = numCycles > 1 ? ` / ${cardFrequency}` : "";

	if (discountedPrice && (discountedPrice < totalPrice)) {
		return (
			<PriceContainer>
				<span>{accounting.formatMoney(totalPrice / 100, currencySymbol)}</span>
				{accounting.formatMoney(discountedPrice/100, currencySymbol)}
				{cardFrequencyDisplay} for {numCycles}{" "}
				{cardFrequencyLong}
			</PriceContainer>
		);
	} else {
		return (
			<PriceContainer>
				{accounting.formatMoney(totalPrice / 100, currencySymbol)}
				{cardFrequencyDisplay} for {numCycles}{" "}
				{cardFrequencyLong}
			</PriceContainer>
		);
	}
};

const MinTermPrice = props => {
	const {
		minPrice,
		currencySymbol,
		cardFrequency,
		discountedPrice,
	} = props;

	if (discountedPrice && (discountedPrice < minPrice)) {
		return (
			<PriceContainer>
				{"From "}
				<span>{accounting.formatMoney(minPrice / 100, currencySymbol)}</span>
				{accounting.formatMoney(discountedPrice/100, currencySymbol)}
				{` / ${cardFrequency}`}
			</PriceContainer>
		);
	} else {
		return (
			<PriceContainer>
				{"From "}
				{accounting.formatMoney(minPrice / 100, currencySymbol)}
				{` / ${cardFrequency}`}
			</PriceContainer>
		);
	}
};

/**
 * calculateDealDiscountedPrice calculates the price of the listing card after deal discount is applied.
 * This has to be done client side for a deal ONLY. Normal coupon discounts are applied on the server.
 * @param {number} basePrice
 * @param {number} discountType - core.CouponDiscountType
 * @param {number} discountAmount
 */
const calculateDealDiscountedPrice = (basePrice, discountType, discountAmount) => {
	let result = basePrice;

	if (discountType === 2) {
		// For flat $ amount don't show discounted price
		// since we now show price per box
		return null;
	}

	if (discountType === 3) {
		// Calc for percent off
		result = result - (result * (discountAmount/100));
	}

	return result;
};

export default class ListingPrice extends PureComponent {
	static propTypes = {
		minPrice: PropTypes.number.isRequired,
		discountedPrice: PropTypes.number,
		discountType: PropTypes.number,
		currencySymbol: PropTypes.string,
		termPrices: PropTypes.arrayOf(
			PropTypes.shape({
				numCycles: PropTypes.number,
				rebillMonths: PropTypes.number,
				rebillWeeks: PropTypes.number,
				cardFrequency: PropTypes.string,
				cardFrequencyLong: PropTypes.string
			})
		),
		priceValue: PropTypes.string,
		priceSort: PropTypes.string,
		dealDiscount: PropTypes.string,
		dealDiscountType: PropTypes.number,
		dealDiscountAmount: PropTypes.number,
	};

	static defaultProps = {
		currencySymbol: "$",
		dealDiscount: null,
		dealDiscountType: null,
		dealDiscountAmount: null,
		discountType: null,
	};

	getMatchingTerm = (priceValue, termPrices) => {
		const priceRangeValues = priceValue.split(":");
		const priceRangeLow = priceRangeValues[0] || 0;
		const priceRangeHigh = priceRangeValues[1] || null;

		let matchingTerms = [];
		if (!priceRangeHigh) {
			matchingTerms = termPrices.filter(
				term => term.price >= priceRangeLow
			);
		} else {
			matchingTerms = termPrices.filter(
				term =>
					term.price >= priceRangeLow && term.price <= priceRangeHigh
			);
		}

		return matchingTerms.length > 0 ? matchingTerms[0] : {};
	};

	render() {
		const {
			currencySymbol,
			minPrice,
			termPrices,
			priceValue,
			priceSort,
			discountedPrice,
			discountType,
			dealDiscount,
			dealDiscountType,
			dealDiscountAmount,
		} = this.props;
		const cardFrequency = "box";

		// Change the display price to discount price if discount is % based.
		let displayDiscountedPrice = discountType !== 2 ? discountedPrice : null;

		// Find the matching term if the user has a price filter selected.
		// TODO: update to work with sorting by price
		if (priceValue && termPrices && !priceSort) {
			const matchingTerm = this.getMatchingTerm(priceValue, termPrices);
			if (matchingTerm) {
				const perBoxPrice = matchingTerm.price / matchingTerm.numCycles;

				let displayDiscountedTermPrice = discountType !== 2 ? matchingTerm.discountedPrice : null;
				if (dealDiscount) {
					displayDiscountedTermPrice = calculateDealDiscountedPrice(perBoxPrice, dealDiscountType, dealDiscountAmount);
				}
				return (
					<FilteredPrice
						totalPrice={perBoxPrice}
						currencySymbol={currencySymbol}
						discountedPrice={displayDiscountedTermPrice}
						cardFrequency={cardFrequency}
						numCycles={matchingTerm.numCycles}
					/>
				);
			}
		}

		if (!minPrice) return null;
		if (dealDiscount) {
			displayDiscountedPrice = calculateDealDiscountedPrice(minPrice, dealDiscountType, dealDiscountAmount);
		}
		return (
			<MinTermPrice
				minPrice={minPrice}
				currencySymbol={currencySymbol}
				cardFrequency={cardFrequency}
				discountedPrice={displayDiscountedPrice}
			/>
		);
	}
}
